import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PS2GraphicsContext = createContext(null);

export function PS2GraphicsProvider({ children }) {
  const [ps2Mode, setPs2Mode] = useState(() => {
    const stored = localStorage.getItem('cc_ps2_mode');
    return stored === null ? true : stored === 'true';
  });
  const [crtFilter, setCrtFilter] = useState(() => {
    return localStorage.getItem('cc_crt_filter') === 'true';
  });
  const [soundFx, setSoundFx] = useState(() => {
    return localStorage.getItem('cc_sound_fx') !== 'false';
  });
  const [ps2CardView, setPs2CardView] = useState(() => {
    return localStorage.getItem('cc_card_view') === 'true';
  });

  // Audio Context singleton for synthesized PS2 Web Audio effects
  const getAudioContext = useCallback(() => {
    if (typeof window === 'undefined') return null;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!window._ps2AudioCtx) {
      window._ps2AudioCtx = new AudioCtx();
    }
    if (window._ps2AudioCtx.state === 'suspended') {
      window._ps2AudioCtx.resume();
    }
    return window._ps2AudioCtx;
  }, []);

  // Web Audio Synthesizer: PS2 Click
  const playPs2Click = useCallback(() => {
    if (!soundFx) return;
    try {
      const ctx = getAudioContext();
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'square';
      osc.frequency.setValueAtTime(440, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(110, ctx.currentTime + 0.05);

      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.05);
    } catch {
      // Ignore audio errors
    }
  }, [soundFx, getAudioContext]);

  // Web Audio Synthesizer: PS2 Select
  const playPs2Select = useCallback(() => {
    if (!soundFx) return;
    try {
      const ctx = getAudioContext();
      if (!ctx) return;
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();

      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
      osc1.frequency.setValueAtTime(659.25, ctx.currentTime + 0.04); // E5

      osc2.type = 'triangle';
      osc2.frequency.setValueAtTime(1046.5, ctx.currentTime);

      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(ctx.destination);

      osc1.start();
      osc2.start();
      osc1.stop(ctx.currentTime + 0.18);
      osc2.stop(ctx.currentTime + 0.18);
    } catch {
      // Ignore audio errors
    }
  }, [soundFx, getAudioContext]);

  // Web Audio Synthesizer: PS2 Emotion Engine Boot Chime
  const playPs2BootChime = useCallback(() => {
    if (!soundFx) return;
    try {
      const ctx = getAudioContext();
      if (!ctx) return;

      const now = ctx.currentTime;
      const notes = [220, 277.18, 329.63, 440, 554.37, 659.25]; // A major chord stack
      
      notes.forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + idx * 0.08);

        gain.gain.setValueAtTime(0.05, now + idx * 0.08);
        gain.gain.exponentialRampToValueAtTime(0.0005, now + idx * 0.08 + 1.2);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now + idx * 0.08);
        osc.stop(now + idx * 0.08 + 1.2);
      });
    } catch {
      // Ignore audio errors
    }
  }, [soundFx, getAudioContext]);

  // Web Audio Synthesizer: Kitchen Receipt Printer / Order Chime
  const playPs2Order = useCallback(() => {
    if (!soundFx) return;
    try {
      const ctx = getAudioContext();
      if (!ctx) return;

      const now = ctx.currentTime;
      [880, 1108.73, 1318.51, 1760].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.setValueAtTime(freq, now + i * 0.07);
        gain.gain.setValueAtTime(0.08, now + i * 0.07);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.07 + 0.15);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + i * 0.07);
        osc.stop(now + i * 0.07 + 0.15);
      });
    } catch {
      // Ignore audio errors
    }
  }, [soundFx, getAudioContext]);

  const togglePs2Mode = () => {
    setPs2Mode(prev => {
      const next = !prev;
      localStorage.setItem('cc_ps2_mode', String(next));
      if (!next) playPs2Click(); // Only play click sound when turning OFF
      return next;
    });
  };

  const toggleCrtFilter = () => {
    setCrtFilter(prev => {
      const next = !prev;
      localStorage.setItem('cc_crt_filter', String(next));
      playPs2Click();
      return next;
    });
  };

  const toggleSoundFx = () => {
    setSoundFx(prev => {
      const next = !prev;
      localStorage.setItem('cc_sound_fx', String(next));
      return next;
    });
  };

  const togglePs2CardView = () => {
    setPs2CardView(prev => {
      const next = !prev;
      localStorage.setItem('cc_card_view', String(next));
      playPs2Click();
      return next;
    });
  };

  useEffect(() => {
    if (ps2Mode) {
      document.documentElement.classList.add('ps2-theme');
    } else {
      document.documentElement.classList.remove('ps2-theme');
    }
  }, [ps2Mode]);

  return (
    <PS2GraphicsContext.Provider
      value={{
        ps2Mode,
        togglePs2Mode,
        crtFilter,
        toggleCrtFilter,
        soundFx,
        toggleSoundFx,
        ps2CardView,
        togglePs2CardView,
        playPs2Click,
        playPs2Select,
        playPs2BootChime,
        playPs2Order,
      }}
    >
      {children}
    </PS2GraphicsContext.Provider>
  );
}

export function usePS2Graphics() {
  const context = useContext(PS2GraphicsContext);
  if (!context) {
    throw new Error('usePS2Graphics must be used within a PS2GraphicsProvider');
  }
  return context;
}
